"use client";
import { useRequestProgramMutation } from "@/app/api/request-program/front";
import { authClient } from "@/lib/authClient";
import { getUserInfo } from "@/lib/userInfo";
import type { Countries } from "@/queries/getCountries";
import { useStore } from "@nanostores/react";
import { atom } from "nanostores";
import { type PropsWithChildren, useId, useState } from "react";
import type { Departure } from "services/travel";
import { BaseForm } from "./BaseForm";
import { hasRequestedProgram, setLastRequestProgram, setRequestProgram } from "./lastRequestProgram";
import { type RequestProgramPosthogOpenEventSource, requestProgramEvent } from "./requestProgramPosthog";

export const requestProgramOpenStore = atom<string | null>(null);

type Props = {
  travelId: number;
  versionId: number;
  departureId: number;
  onSuccess?: () => void;
  countries: Countries;
  isAvailable: boolean;
  travelTitle: string;
  travelImage: {
    id: number;
    realname: string;
    name: string;
    caption: string;
  } | null;
  departure: Pick<Departure, "dateStart" | "dateEnd" | "infosupUrl">;
  travelCountries: Array<{ title: string; alias: string | null }>;
  small?: boolean;
  className?: string;
  source: RequestProgramPosthogOpenEventSource;
};
export const RequestProgram = ({
  travelId,
  versionId,
  departureId,
  onSuccess,
  children,
  countries,
  isAvailable,
  travelTitle,
  travelImage,
  departure,
  travelCountries,
  small = false,
  className,
  source,
}: PropsWithChildren<Props>) => {
  const id = useId();
  const show = useStore(requestProgramOpenStore);
  const [isLoading, setIsLoading] = useState(false);
  const [request] = useRequestProgramMutation({
    onCompleted: (data) => {
      if (data.success) {
        setLastRequestProgram();
        setRequestProgram();
        if (onSuccess) onSuccess();
      }
    },
  });

  if (!isAvailable) return null;

  const handleClick = async () => {
    if (isLoading) return;

    setIsLoading(true);

    try {
      const openProgram = () => {
        const a = document.createElement("a");
        a.href = departure.infosupUrl;
        a.target = "_blank";
        a.click();
        a.remove();
      };

      const { data } = await authClient.getSession();

      if (data !== null) {
        requestProgramEvent({
          step: "direct",
          source,
          travelId,
          versionId,
          departureId,
        });
        setLastRequestProgram();
        request({
          type: "authenticated",
          departureId,
        });
        openProgram();
        return;
      }

      const userInfo = await getUserInfo();

      if (userInfo) {
        setLastRequestProgram();
        requestProgramEvent({
          step: "direct",
          source,
          travelId,
          versionId,
          departureId,
        });
        request({
          type: "anonymous",
          email: userInfo.email,
          phone: userInfo.telephone,
          departureId,
          sendEmail: false,
        });
        openProgram();
        return;
      }

      if (hasRequestedProgram()) {
        setLastRequestProgram();
        requestProgramEvent({
          step: "direct",
          source,
          travelId,
          versionId,
          departureId,
        });
        openProgram();
        return;
      }

      requestProgramOpenStore.set(id);
      requestProgramEvent({
        step: "open",
        source,
        travelId,
        versionId,
        departureId,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {children && (
        <div onClick={handleClick} className={className}>
          {children}
        </div>
      )}
      {show === id && (
        <BaseForm
          departureId={departureId}
          onSuccess={onSuccess}
          onHide={() => {
            requestProgramEvent({
              step: "close",
              source,
              travelId,
              versionId,
              departureId,
            });
            requestProgramOpenStore.set(null);
          }}
          countries={countries}
          travelId={travelId}
          versionId={versionId}
          travelTitle={travelTitle}
          travelImage={travelImage}
          departure={departure}
          travelCountries={travelCountries}
          small={small}
          programUrl={departure.infosupUrl}
          source={source}
        />
      )}
    </>
  );
};
